import { GameScene } from './game/Scene';
import { Game } from './game/Game';
import { AnimatedModelLoader } from './utils/AnimatedModelLoader';
import { InputHandler } from './utils/InputHandler';
import { UIManager } from './ui/UIManager';
import type { Character } from './schemas';
import { initializeFirebase } from './firebase/config';
import { signInAnonymous } from './firebase/auth';
import { logger } from './utils/logger';

logger.log('ゆゆ式 3Dランナーゲーム');

// グローバル変数
let gameScene: GameScene | null = null;
let game: Game | null = null;
let animatedModelLoader: AnimatedModelLoader | null = null;
let inputHandler: InputHandler | null = null;
let uiManager: UIManager | null = null;
let animationId: number | null = null;
let currentCharacter: Character | null = null;

/**
 * アプリケーションの初期化
 */
async function init() {
  const canvas = document.getElementById('game-canvas') as HTMLCanvasElement;
  if (!canvas) {
    logger.error('Canvas element not found');
    return;
  }

  // Firebaseの初期化
  const firebase = initializeFirebase();
  if (firebase) {
    // 匿名認証
    await signInAnonymous();
  } else {
    logger.warn('Firebase機能は無効です。ランキング機能を使用する場合は環境変数を設定してください。');
  }

  // シーンの初期化
  gameScene = new GameScene(canvas);
  animatedModelLoader = new AnimatedModelLoader();
  game = new Game(gameScene);
  uiManager = new UIManager();

  // スコア更新のコールバック設定
  game.setOnScoreUpdate((score) => {
    if (uiManager) {
      uiManager.updateScore(score);
    }
  });

  // ゲームオーバーのコールバック設定
  game.setOnGameOver((score) => {
    logger.log('Game Over with score:', score);
    if (uiManager) {
      uiManager.showGameOverScreen(score);
    }
  });

  // 難易度上昇のコールバック設定
  game.setOnDifficultyIncrease(() => {
    if (uiManager) {
      uiManager.showDifficultyMessage();
    }
  });

  // UIマネージャーのコールバック設定
  uiManager.setOnStartGame((character: Character) => {
    currentCharacter = character;
    startGame(character);
  });

  uiManager.setOnRetry(() => {
    if (currentCharacter) {
      startGame(currentCharacter);
    }
  });

  uiManager.setOnBackToStart(() => {
    if (game) {
      game.reset();
    }
    if (uiManager) {
      uiManager.showStartScreen();
    }
  });

  // 入力ハンドラーの初期化
  inputHandler = new InputHandler(() => {
    if (game) {
      game.jump();
    }
  });

  // スタート画面を表示
  uiManager.showStartScreen();

  // レンダリングループの開始
  animate();

  logger.log('Game initialized');
}

/**
 * ゲームを開始
 */
async function startGame(character: Character) {
  if (!game || !animatedModelLoader || !uiManager) return;

  // ゲームをリセット
  game.reset();

  // ローディング表示
  uiManager.showLoadingIndicator();

  try {
    // アニメーション付きモデルを読み込む
    logger.log(`Loading animated model for ${character.name}...`);
    const modelData = await animatedModelLoader.loadAnimations(character);
    logger.log(`Successfully loaded animated model for ${character.name}`);

    // ローディング非表示
    uiManager.hideLoadingIndicator();

    // ゲームを開始
    game.start(character, modelData);

    // ゲーム画面を表示
    uiManager.showGameScreen();

    logger.log('Game started with character:', character.name);
  } catch (error) {
    logger.error(`Failed to load animated model for ${character.name}:`, error);
    uiManager.hideLoadingIndicator();
    alert(`モデルの読み込みに失敗しました: ${error}`);
    uiManager.showStartScreen();
  }
}

/**
 * アニメーションループ
 */
function animate() {
  animationId = requestAnimationFrame(animate);

  // ゲームの更新（playing状態の時のみ）
  if (game) {
    const gameState = game.getState();
    
    // ゲームがプレイ中の場合のみ更新とレンダリング
    if (gameState === 'playing') {
      game.update();
      
      // シーンのレンダリング
      if (gameScene) {
        gameScene.render();
      }
    } else {
      // ゲームが停止している時は、UI表示のために最低限のレンダリングのみ
      // （背景などが表示されるように）
      if (gameScene && gameState !== 'ready') {
        gameScene.render();
      }
    }
  } else {
    // ゲームが初期化されていない場合でも、シーンがあればレンダリング
    if (gameScene) {
      gameScene.render();
    }
  }
}

/**
 * クリーンアップ
 */
function cleanup() {
  if (animationId !== null) {
    cancelAnimationFrame(animationId);
  }

  if (inputHandler) {
    inputHandler.dispose();
  }

  if (game) {
    game.reset();
  }

  if (gameScene) {
    gameScene.dispose();
  }
}

// ページ読み込み時に初期化
window.addEventListener('load', init);

// ページ離脱時にクリーンアップ
window.addEventListener('beforeunload', cleanup);

export {};
